// ScoringPresetSelector.tsx - Modal for selecting scoring presets

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import { ScoringPreset, ScoringPresetConfig } from '../../types/ScoringConfiguration';
import { ScoringConfigurationService } from '../../services/ScoringConfigurationService';
import { StandardCard } from '../ui';

interface ScoringPresetSelectorProps {
  onSelect: (preset: ScoringPreset) => void;
  onClose: () => void;
}

export const ScoringPresetSelector: React.FC<ScoringPresetSelectorProps> = ({
  onSelect,
  onClose
}) => {
  const { isDark } = useTheme();
  const presets = ScoringConfigurationService.getAvailablePresets();

  const getPresetIcon = (preset: ScoringPreset): string => {
    const icons: Record<ScoringPreset, string> = {
      default: '⚖️',
      balanced: '🎯',
      forgiving: '🤗',
      hardcore: '💀',
      educational: '📚'
    };
    return icons[preset] || '📊';
  };

  const getPresetColor = (preset: ScoringPreset): string => {
    if (isDark) {
      const colors: Record<ScoringPreset, string> = {
        default: 'border-blue-700 bg-blue-900/20',
        balanced: 'border-green-700 bg-green-900/20',
        forgiving: 'border-purple-700 bg-purple-900/20',
        hardcore: 'border-red-700 bg-red-900/20',
        educational: 'border-yellow-700 bg-yellow-900/20'
      };
      return colors[preset] || 'border-gray-700 bg-gray-900/20';
    } else {
      const colors: Record<ScoringPreset, string> = {
        default: 'border-blue-300 bg-blue-50',
        balanced: 'border-green-300 bg-green-50',
        forgiving: 'border-purple-300 bg-purple-50',
        hardcore: 'border-red-300 bg-red-50',
        educational: 'border-yellow-300 bg-yellow-50'
      };
      return colors[preset] || 'border-gray-300 bg-gray-50';
    }
  };

  const getPresetDetails = (preset: ScoringPresetConfig): string[] => {
    const details: string[] = [];
    
    if (preset.config.difficultyMultiplier) {
      const difficulty = preset.config.difficultyMultiplier;
      if (difficulty > 1.2) details.push('Very Hard');
      else if (difficulty > 1) details.push('Challenging');
      else if (difficulty < 0.8) details.push('Easy Mode');
      else details.push('Normal Difficulty');
    }

    if (preset.config.baseScores) {
      const avgBase = Object.values(preset.config.baseScores).reduce((a, b) => a + b, 0) / 4;
      details.push(`Base Score: ${Math.round(avgBase)}`);
    }

    if (preset.config.maxIncidentMitigation !== undefined) {
      details.push(`Max Mitigation: ${Math.round(preset.config.maxIncidentMitigation * 100)}%`);
    }

    return details;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className={`w-full max-w-2xl rounded-xl shadow-2xl p-6 ${
            isDark ? 'bg-gray-900' : 'bg-white'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Choose a Preset
              </h2>
              <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Start with a pre-configured scoring system
              </p>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${
                isDark ? 'text-gray-400' : 'text-gray-600'
              }`}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Preset Grid */}
          <div className="grid gap-4 mb-6">
            {presets.map((preset) => (
              <motion.div
                key={preset.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <StandardCard
                  variant="outlined"
                  padding="large"
                  hoverable
                  className={`cursor-pointer border-2 ${getPresetColor(preset.id)}`}
                  onClick={() => onSelect(preset.id)}
                >
                  <div className="flex items-start gap-4">
                    <div className="text-4xl">{getPresetIcon(preset.id)}</div>
                    <div className="flex-1">
                      <h3 className={`text-lg font-semibold mb-1 ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>
                        {preset.name}
                      </h3>
                      <p className={`text-sm mb-3 ${
                        isDark ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        {preset.description}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {getPresetDetails(preset).map((detail, index) => (
                          <span
                            key={index}
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              isDark
                                ? 'bg-gray-800 text-gray-300'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {detail}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="text-gray-400">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"/>
                      </svg>
                    </div>
                  </div>
                </StandardCard>
              </motion.div>
            ))}
          </div>

          {/* Info Box */}
          <StandardCard variant="default" padding="small">
            <p className="text-sm">
              <strong>Tip:</strong> You can further customize any preset after applying it. 
              Choose the one that best matches your intended difficulty and gameplay style.
            </p>
          </StandardCard>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};